import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Chip,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { useCreateChatGroup, useUpdateChatGroup, useListOrgsForChat, useListChatGroupTags } from '../services/appService';
import Autocomplete from '@mui/material/Autocomplete';

function ChatGroupFormModal({ open, onClose, onSave, group, appId }) {
    const [formData, setFormData] = useState({});
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [error, setError] = useState('');

    const createGroupHook = useCreateChatGroup();
    const updateGroupHook = useUpdateChatGroup();
    const listOrgsHook = useListOrgsForChat();
    const listTagsHook = useListChatGroupTags();

    const isLoading = createGroupHook.loading || updateGroupHook.loading || loadingOrgs || loadingTags;

    // Fetch organizations when modal opens or appId changes
    useEffect(() => {
        if (open && appId) {
            const fetchOrgs = async () => {
                setLoadingOrgs(true);
                setError('');
                try {
                    const orgs = await listOrgsHook(appId);
                    setOrganizations(orgs || []);
                } catch (err) {
                    console.error("Failed to fetch organizations:", err);
                    setError('Failed to load organizations list. Broadcast groups cannot be assigned.');
                    setOrganizations([]); // Ensure it's empty on error
                } finally {
                    setLoadingOrgs(false);
                }
            };
            fetchOrgs();
        }
    }, [open, appId, listOrgsHook]);

    // Fetch available chat tags
    useEffect(() => {
        if (open && appId) {
            const fetchTags = async () => {
                setLoadingTags(true);
                setError('');
                try {
                    const tags = await listTagsHook(appId);
                    setAvailableTags((tags || []).filter(tag => !tag.is_admin_only).map(tag => tag.name));
                } catch (err) {
                    console.error("Failed to fetch chat tags:", err);
                    setError(prev => prev ? `${prev}\nFailed to load tags list.` : 'Failed to load tags list.');
                    setAvailableTags([]);
                } finally {
                    setLoadingTags(false);
                }
            };
            fetchTags();
        }
    }, [open, appId, listTagsHook]);

    // Initialize form data when group or modal state changes
    useEffect(() => {
        if (open) {
            if (group) {
                // Editing existing group
                setFormData({
                    name: group.name || '',
                    type: group.type || 'open_group',
                    description: group.description || '',
                    tags: group.tags || [],
                    is_pinned: group.is_pinned || false,
                    organization_id: group.organization_id || '', // Use empty string for no selection
                    is_active: group.is_active !== undefined ? group.is_active : true,
                });
            } else {
                // Creating new group - set defaults
                setFormData({
                    name: '',
                    type: 'open_group', // Default type
                    description: '',
                    tags: [],
                    is_pinned: false,
                    organization_id: '',
                    is_active: true,
                });
            }
            setError(''); // Reset error on open/group change
        } else {
             // Reset form when modal closes
            setFormData({}); 
            setError('');
            setOrganizations([]); // Clear orgs when closed
        }
    }, [group, open]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        // If type changes to non-broadcast, clear organization_id
        if (name === 'type' && value !== 'broadcast') {
            setFormData((prev) => ({ ...prev, organization_id: '' }));
        }
    };

    // Handler for Autocomplete tag selection
    const handleTagsChange = (event, newValue) => {
        setFormData((prev) => ({
            ...prev,
            tags: newValue, // newValue is already an array of strings
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(''); // Clear previous errors

        // Validation
        if (!formData.name) {
            setError('Group name is required.');
            return;
        }
        if (formData.type === 'broadcast' && !formData.organization_id) {
             setError('An organization must be selected for broadcast groups.');
             return;
        }

        const dataToSave = {
            ...formData,
            // Ensure organization_id is null if not broadcast type
            organization_id: formData.type === 'broadcast' ? formData.organization_id : null,
        };

        try {
            if (group) {
                // Update existing group
                await updateGroupHook(appId, group.id, dataToSave);
            } else {
                // Create new group
                await createGroupHook(appId, dataToSave);
            }
            onSave(); // Call parent's save handler (closes modal, refreshes list)
        } catch (err) {
            console.error('Failed to save chat group:', err);
            setError(err.message || `Failed to ${group ? 'update' : 'create'} chat group.`);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{group ? 'Edit Chat Group' : 'Create New Chat Group'}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><CircularProgress /></Box>}

                    <TextField
                        autoFocus
                        margin="dense"
                        name="name"
                        label="Group Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.name || ''}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                    />
                    <TextField
                        margin="dense"
                        name="description"
                        label="Description (Optional)"
                        type="text"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        value={formData.description || ''}
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                    <FormControl fullWidth margin="dense" required disabled={isLoading}>
                        <InputLabel id="type-select-label">Group Type</InputLabel>
                        <Select
                            labelId="type-select-label"
                            name="type"
                            value={formData.type || ''}
                            label="Group Type"
                            onChange={handleChange}
                        >
                            <MenuItem value="open_group">Open Group</MenuItem>
                            <MenuItem value="broadcast">Broadcast</MenuItem>
                            {/* Add other types like 'bot' if needed */}
                        </Select>
                    </FormControl>

                    {formData.type === 'broadcast' && (
                        <FormControl fullWidth margin="dense" required disabled={isLoading || loadingOrgs || organizations.length === 0}>
                            <InputLabel id="organization-select-label">Organization</InputLabel>
                            <Select
                                labelId="organization-select-label"
                                name="organization_id"
                                value={formData.organization_id || ''}
                                label="Organization"
                                onChange={handleChange}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {organizations.map((org) => (
                                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                                ))}
                            </Select>
                             {organizations.length === 0 && !loadingOrgs && <Alert severity="warning" sx={{mt: 1}}>No organizations available to assign.</Alert>}
                        </FormControl>
                    )}

                    <Autocomplete
                        multiple
                        id="tags-select"
                        options={availableTags}
                        value={formData.tags || []}
                        onChange={handleTagsChange}
                        disabled={isLoading || loadingTags}
                        loading={loadingTags}
                        getOptionLabel={(option) => option}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Tags (Optional)"
                                placeholder="Select tags"
                                margin="dense"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {loadingTags ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                            ))
                        }
                    />

                    <FormGroup sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={formData.is_pinned || false} onChange={handleChange} name="is_pinned" />}
                            label="Pinned Group"
                            disabled={isLoading}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={formData.is_active !== undefined ? formData.is_active : true} onChange={handleChange} name="is_active" />}
                            label="Active Group"
                             disabled={isLoading}
                        />
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={isLoading}>
                        {isLoading ? <CircularProgress size={24} /> : (group ? 'Save Changes' : 'Create Group')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default ChatGroupFormModal; 